import { DisconnectOutlined, LaptopOutlined, ReloadOutlined } from '@ant-design/icons';
import {
  Button,
  Empty,
  List,
  Modal,
  Popconfirm,
  Space,
  Tag,
  Typography,
  message,
} from 'antd';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { useEffect, useState } from 'react';

dayjs.extend(relativeTime);
import {
  api,
  type SlideLicenseConnection,
  type SlideLicenseRow,
} from '../lib/api';

const { Text } = Typography;

type Props = {
  open: boolean;
  license: SlideLicenseRow | null;
  onClose: () => void;
};

/**
 * Shows every slides machine currently holding a live WebSocket against this
 * license's SlideRoom, with a per-row Disconnect button. Polls every
 * three seconds while open so a freshly-kicked machine drops out of the
 * list visibly, and a machine that reconnects (e.g. user pressed Connect
 * again after the kick) shows up just as quickly. Polling stops the
 * moment the modal is closed.
 */
export function SlideConnectionsModal({ open, license, onClose }: Props) {
  const [connections, setConnections] = useState<SlideLicenseConnection[]>([]);
  const [loading, setLoading] = useState(false);
  const [kicking, setKicking] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !license) return;
    let cancelled = false;
    const load = async () => {
      try {
        const res = await api<{ apps: SlideLicenseConnection[] }>(
          `/api/slide-licenses/${license.id}/connections`,
        );
        if (!cancelled) setConnections(res.apps ?? []);
      } catch (err) {
        if (!cancelled) {
          message.error(
            (err as { error?: string }).error ?? 'Failed to load connections',
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    setLoading(true);
    void load();
    const t = window.setInterval(load, 3000);
    return () => {
      cancelled = true;
      window.clearInterval(t);
    };
  }, [open, license]);

  const disconnect = async (conn: SlideLicenseConnection) => {
    if (!license) return;
    setKicking(conn.id);
    try {
      await api(
        `/api/slide-licenses/${license.id}/connections/${conn.id}/disconnect`,
        { method: 'POST' },
      );
      // Drop it from the local list immediately so the row disappears
      // without waiting for the next poll tick.
      setConnections((prev) => prev.filter((c) => c.id !== conn.id));
      message.success(`Disconnected ${conn.name}`);
    } catch (err) {
      message.error((err as { error?: string }).error ?? 'Disconnect failed');
    } finally {
      setKicking(null);
    }
  };

  return (
    <Modal
      title={
        license ? `Connected machines — ${license.name}` : 'Connected machines'
      }
      open={open}
      onCancel={onClose}
      footer={[
        <Button key="close" onClick={onClose}>
          Close
        </Button>,
      ]}
      width={560}
    >
      {connections.length === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            loading
              ? 'Checking…'
              : 'No machines are connected to this license.'
          }
        />
      ) : (
        <List
          dataSource={connections}
          renderItem={(conn) => (
            <List.Item
              actions={[
                <Popconfirm
                  key="disconnect"
                  title={`Disconnect ${conn.name}?`}
                  description="The Slide Control app on that machine will show a notice and stop responding to Test Next until someone clicks Connect again there."
                  okText="Disconnect"
                  okButtonProps={{ danger: true }}
                  onConfirm={() => disconnect(conn)}
                >
                  <Button
                    danger
                    size="small"
                    icon={<DisconnectOutlined />}
                    loading={kicking === conn.id}
                  >
                    Disconnect
                  </Button>
                </Popconfirm>,
              ]}
            >
              <List.Item.Meta
                avatar={<LaptopOutlined style={{ fontSize: 20 }} />}
                title={
                  <Space size="small">
                    <Text strong>{conn.name}</Text>
                    {conn.primary ? (
                      <Tag color="blue">Primary</Tag>
                    ) : (
                      <Tag>Backup</Tag>
                    )}
                  </Space>
                }
                description={
                  <Space size="small" wrap>
                    {conn.slideNumber !== null && conn.totalSlides !== null ? (
                      <Text type="secondary">
                        Slide {conn.slideNumber} / {conn.totalSlides}
                      </Text>
                    ) : (
                      <Text type="secondary">Idle (no slide read yet)</Text>
                    )}
                    <Text type="secondary">·</Text>
                    <Text type="secondary">
                      Connected {dayjs(conn.connectedAt).fromNow()}
                    </Text>
                  </Space>
                }
              />
            </List.Item>
          )}
        />
      )}
      <div style={{ marginTop: 8, color: '#888', fontSize: 12 }}>
        <ReloadOutlined /> Refreshing every 3 seconds.
      </div>
    </Modal>
  );
}
